/**
 * **Einzahl und Mehrzahl in einem Satz** (Lucas Entscheidung vom 07.10.2026,
 * fixes-p9 „Vorzulegen" 3).
 *
 * Bis hierher stand in `de.json` „{zahl} Tage" — und am Bildschirm „1 Tage",
 * „1 Punkte", „seit 1 Tagen". P9-06 hatte das für die Fehlersätze mit eigenen
 * Einzahlschlüsseln (`fehler.<name>Einer`) gelöst; rund dreihundert weitere
 * Sätze hatten keine Einzahl. Statt jedem Satz eine Schwester zu geben, trägt
 * der Satz beide Formen selbst, in der Schreibweise von ICU (die auch jede
 * Übersetzungsbibliothek liest, falls je eine kommt):
 *
 *     "{zahl, plural, one {# Tag} other {# Tage}}"
 *     "seit {zahl, plural, one {# Tag} other {# Tagen}}"
 *     "{zahl, plural, one {# Preis ist} other {# Preise sind}} heute andere"
 *
 * - `#` steht für den Wert, so wie er hereinkommt — formatiert bleibt
 *   formatiert („1,5").
 * - `=0 {…}` (oder jede andere Zahl) geht vor `one`/`other`.
 * - Innen dürfen weitere Platzhalter stehen (`{name}`), auch `<b>`.
 * - Fehlt der Wert, gilt `other`, und `#` bleibt `{zahl}` — wie ein fehlender
 *   Platzhalter sonst auch (der Satz soll auffallen, nicht verschwinden).
 *
 * **Warum hier**: Die Oberfläche (`apps/web/src/i18n`) und der Server
 * (`satz()` in `apps/api/src/dienste/meldungsmail.ts`, Mails, Papiere) lesen
 * dieselbe `de.json`; beide hängen an `@mrmachine/fach`. Eine zweite Regel
 * je Seite wäre der Ort, an dem Glocke und Mail auseinanderlaufen. Kein
 * sichtbarer Text steht in dieser Datei — nur die Regel.
 */
/**
 * Ist der Wert „eins" im Sinne der deutschen Grammatik? Genau 1 — nicht 1,5,
 * nicht 0. Eine schon formatierte Zahl („1", „-1") zählt wie die Zahl; „1,0"
 * oder „1,5 Std." nicht (im Deutschen „1,5 Stunden").
 */
export function istEinzahl(wert) {
    if (typeof wert === 'number')
        return Math.abs(wert) === 1;
    if (typeof wert === 'string')
        return /^\s*[-−]?1\s*$/.test(wert);
    return false;
}
/** Der Wert als Zahl, wenn er eindeutig eine ganze ist — für `=0 {…}`. */
function alsGanzzahl(wert) {
    if (typeof wert === 'number' && Number.isFinite(wert))
        return wert;
    if (typeof wert === 'string' && /^\s*-?\d+\s*$/.test(wert))
        return Number(wert);
    return null;
}
const KOPF = /\{(\w+)\s*,\s*plural\s*,/g;
/** Liest ab `i` einen Block `{…}` mit verschachtelten Klammern; gibt Inhalt und Ende zurück. */
function block(satz, i) {
    if (satz[i] !== '{')
        return null;
    let tiefe = 0;
    for (let j = i; j < satz.length; j++) {
        if (satz[j] === '{')
            tiefe++;
        else if (satz[j] === '}') {
            tiefe--;
            if (tiefe === 0)
                return { inhalt: satz.slice(i + 1, j), ende: j + 1 };
        }
    }
    return null;
}
/**
 * Löst jede Stelle `{name, plural, …}` im Satz auf. Was keine solche Stelle
 * ist, bleibt unberührt — die gewöhnlichen Platzhalter setzt der Aufrufer ein.
 * Ein kaputter Ausdruck bleibt stehen, wie er ist (auffallen statt verschlucken).
 */
export function mehrzahlAufloesen(satz, werte = {}) {
    if (!satz.includes('plural'))
        return satz;
    let aus = '';
    let zuletzt = 0;
    KOPF.lastIndex = 0;
    let treffer;
    while ((treffer = KOPF.exec(satz)) !== null) {
        const name = treffer[1];
        let i = treffer.index + treffer[0].length;
        const faelle = new Map();
        let ende = -1;
        for (;;) {
            while (i < satz.length && /\s/.test(satz[i]))
                i++;
            if (satz[i] === '}') {
                ende = i + 1;
                break;
            }
            const wahl = /^(=-?\d+|zero|one|two|few|many|other)/.exec(satz.slice(i));
            if (!wahl)
                break;
            i += wahl[0].length;
            while (i < satz.length && /\s/.test(satz[i]))
                i++;
            const b = block(satz, i);
            if (!b)
                break;
            faelle.set(wahl[0], b.inhalt);
            i = b.ende;
        }
        if (ende < 0 || !faelle.has('other'))
            continue;
        const da = Object.prototype.hasOwnProperty.call(werte, name) && werte[name] !== null && werte[name] !== undefined;
        const wert = da ? werte[name] : undefined;
        const ganz = da ? alsGanzzahl(wert) : null;
        const text = (ganz !== null ? faelle.get(`=${ganz}`) : undefined) ??
            (da && istEinzahl(wert) ? faelle.get('one') : undefined) ??
            faelle.get('other');
        aus += satz.slice(zuletzt, treffer.index) + mehrzahlAufloesen(text.replace(/#/g, da ? String(wert) : `{${name}}`), werte);
        zuletzt = ende;
        KOPF.lastIndex = ende;
    }
    return aus + satz.slice(zuletzt);
}
/**
 * Für die wenigen Stellen im Server, die einen Satz ohne `de.json` bauen
 * (Papiere, Export-Beschreibung — `apps/api/src/mitte/belegzeitraum.ts`
 * erklärt, warum): die Zahl mit dem passenden Wort.
 */
export function mitZahlwort(zahl, eins, viele) {
    return `${zahl} ${istEinzahl(zahl) ? eins : viele}`;
}
//# sourceMappingURL=mehrzahl.js.map